import { CircularProgress, type CircularProgressProps } from "@mui/material";

import { designTokens } from "@/shared/styles/tokens";

export type LoadingIndicatorSize = "small" | "medium" | "large";

export interface LoadingIndicatorProps
  extends Omit<CircularProgressProps, "size"> {
  /** Accessible description announced by assistive technology. */
  label?: string;
  /** Prefer a named, consistent size. Numeric values remain supported for compact legacy layouts. */
  size?: LoadingIndicatorSize | CircularProgressProps["size"];
}

const indicatorSizes: Record<LoadingIndicatorSize, number> = {
  small: 16,
  medium: 24,
  large: 32,
};

const resolveIndicatorSize = (
  size: LoadingIndicatorProps["size"],
): CircularProgressProps["size"] =>
  typeof size === "string" && size in indicatorSizes
    ? indicatorSizes[size as LoadingIndicatorSize]
    : size;

const LoadingIndicator = ({
  label = "Loading",
  size = "medium",
  color,
  sx,
  ...props
}: LoadingIndicatorProps) => (
  <CircularProgress
    aria-label={label}
    color={color}
    role="status"
    size={resolveIndicatorSize(size)}
    sx={[
      ...(color ? [] : [{ color: designTokens.color.action.primary }]),
      ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
    ]}
    {...props}
  />
);

export default LoadingIndicator;
